import { useState, useEffect, useMemo, useCallback, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import iconUrl from "leaflet/dist/images/marker-icon.png";
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";
import {
  Camera,
  Clock,
  TickCircle,
  CloseCircle,
  Import,
  Folder2,
  Profile,
  Calendar,
  Mobile,
  Location,
} from "iconsax-react";
import cn from "classnames";
import { ModalForm, Badge, Button, ImageViewer, Skeleton } from "@/shared/ui";
import type { BadgeColor } from "@/shared/ui/Badge";
import { useAuth } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts/permissions";
import { formatDateForDisplay, extractErrorMessage, BOARD_PAGE_ROUTE } from "@/shared/utils";
import CheckIcon from "@/shared/assets/icons/CheckIcon";
import CloseIcon from "@/shared/assets/icons/CloseIcon";
import { useGetFSMSubmissionQuery, useApproveFSMSubmissionMutation, useLazyGetFSMPdfQuery } from "../api/fsmApi";
import type { FSMPhoto, FSMStatus } from "../types/fsm.types";
import FSMRejectModal from "./FSMRejectModal";

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({ iconUrl, iconRetinaUrl, shadowUrl });

interface Props {
  submissionId: string;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const STATUS_CONFIG: Record<FSMStatus, { color: BadgeColor; labelKey: string; icon: ReactNode }> = {
  draft: {
    color: "gray",
    labelKey: "fsmReview.statusLabels.draft",
    icon: <Camera size={14} color="currentColor" />,
  },
  pending_review: {
    color: "notice",
    labelKey: "fsmReview.statusLabels.pending_review",
    icon: <Clock size={14} color="currentColor" variant="Bold" />,
  },
  approved: {
    color: "positive",
    labelKey: "fsmReview.statusLabels.approved",
    icon: <TickCircle size={14} color="currentColor" variant="Bold" />,
  },
  rejected: {
    color: "negative",
    labelKey: "fsmReview.statusLabels.rejected",
    icon: <CloseCircle size={14} color="currentColor" variant="Bold" />,
  },
};

export default function FSMReviewModal({ submissionId, onClose, onSuccess, onError }: Props) {
  const { t } = useTranslation("PendingActionsPage");
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { data: submission, isLoading, error, refetch: refetchSubmission } = useGetFSMSubmissionQuery(submissionId);
  const [approveFSMSubmission] = useApproveFSMSubmissionMutation();
  const [triggerGetPdf] = useLazyGetFSMPdfQuery();
  const [isApproving, setIsApproving] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [viewingPhoto, setViewingPhoto] = useState<FSMPhoto | null>(null);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  const canReview = hasPermission(PERMISSIONS.FSM.REVIEW_REPORT) && submission?.status === "pending_review";

  const verificationPhotos = useMemo(
    function filterVerification() {
      if (!submission?.photos) return [];
      return submission.photos.filter((p) => p.kind === "verification").sort((a, b) => a.position - b.position);
    },
    [submission?.photos],
  );

  const activityPhotos = useMemo(
    function filterActivity() {
      if (!submission?.photos) return [];
      return submission.photos
        .filter((p) => p.kind === "activity")
        .sort((a, b) => a.position - b.position || (a.captured_at || "").localeCompare(b.captured_at || ""));
    },
    [submission?.photos],
  );

  const hasProcessingPhotos = useMemo(
    function checkProcessing() {
      return (
        submission?.photos.some((p) => p.processing_status !== "ready" && p.processing_status !== "failed") ?? false
      );
    },
    [submission?.photos],
  );

  const hasCoordinates = submission?.centroid_lat != null && submission?.centroid_lng != null;

  const handleRefetchOnFocus = useCallback(
    function refetchOnFocus() {
      refetchSubmission();
    },
    [refetchSubmission],
  );

  useEffect(
    function windowFocusRefetch() {
      window.addEventListener("focus", handleRefetchOnFocus);
      return () => window.removeEventListener("focus", handleRefetchOnFocus);
    },
    [handleRefetchOnFocus],
  );

  async function handleApprove() {
    if (isApproving) return;
    setIsApproving(true);
    try {
      await approveFSMSubmission(submissionId).unwrap();
      onSuccess(t("fsmReview.approveSuccess"));
    } catch (err) {
      onError(extractErrorMessage(err, "errors.fsmApprove", t));
    } finally {
      setIsApproving(false);
    }
  }

  async function handleDownloadPdf() {
    if (isDownloadingPdf) return;
    setIsDownloadingPdf(true);
    try {
      const result = await triggerGetPdf(submissionId).unwrap();
      window.open(result.pdf_url, "_blank");
    } catch {
      onError(t("fsmReview.pdfFailed"));
    } finally {
      setIsDownloadingPdf(false);
    }
  }

  function handleRejectSuccess(message: string) {
    setShowRejectModal(false);
    onSuccess(message);
  }

  function handleRejectError(message: string) {
    setShowRejectModal(false);
    onError(message);
  }

  function handleOpenTask() {
    if (!submission) return;
    onClose();
    navigate(`${BOARD_PAGE_ROUTE}?project=${submission.project}&taskId=${submission.task}`);
  }

  function renderPhotoTile(photo: FSMPhoto) {
    const isReady = photo.processing_status === "ready";
    const src = photo.thumb_url || photo.image_url;

    if (!isReady || !src) {
      return (
        <div
          key={photo.id}
          className="w-50 aspect-square shrink-0 surface-secondary-fill radius-sm flex items-center justify-center">
          <Camera size={24} color="currentColor" className="content-base-low" />
        </div>
      );
    }

    return (
      <button
        key={photo.id}
        type="button"
        className="w-50 aspect-square shrink-0 radius-sm overflow-hidden cursor-pointer group relative"
        onClick={() => setViewingPhoto(photo)}>
        <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
      </button>
    );
  }

  const statusConfig = submission ? STATUS_CONFIG[submission.status] : null;
  const showDownload = submission?.pdf_status === "ready";
  const hasFooter = canReview || showDownload;

  const footer = hasFooter ? (
    <div className="flex flex-col gap-3 w-full">
      {canReview && (
        <Button variant="primary" className="w-full" disabled={isApproving} onClick={handleApprove}>
          <CheckIcon />
          {isApproving ? t("fsmReview.approving") : t("fsmReview.approve")}
        </Button>
      )}
      {(showDownload || canReview) && (
        <div className="flex gap-3 w-full">
          {showDownload && (
            <Button variant="secondary" className="flex-1" disabled={isDownloadingPdf} onClick={handleDownloadPdf}>
              <Import size={16} color="currentColor" />
              {t("fsmReview.downloadPdf")}
            </Button>
          )}
          {canReview && (
            <Button
              variant="danger"
              className={showDownload ? "flex-1" : "w-full"}
              disabled={isApproving}
              onClick={() => setShowRejectModal(true)}>
              <CloseIcon size={16} />
              {t("fsmReview.reject")}
            </Button>
          )}
        </div>
      )}
    </div>
  ) : undefined;

  if (!isLoading && error && !submission) {
    return (
      <ModalForm icon={Camera} onClose={onClose} width="w-3xl">
        <div className="flex items-center justify-center py-12">
          <p className="text-body-regular-md content-action-negative">{t("errors.fsmLoad")}</p>
        </div>
      </ModalForm>
    );
  }

  return (
    <>
      <ModalForm icon={Camera} onClose={onClose} resize width="w-3xl" footer={footer}>
        {isLoading || !submission ? (
          <div className="flex flex-col gap-4 p-1">
            <Skeleton height={28} width={300} borderRadius={4} />
            <div className="flex flex-col">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="py-3 flex items-center gap-3 border-b surface-base-stroke">
                  <Skeleton height={16} width={16} borderRadius={4} />
                  <Skeleton height={20} width={120} borderRadius={4} />
                  <div className="flex-1" />
                  <Skeleton height={20} width={160} borderRadius={4} />
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <Skeleton width={200} height={200} borderRadius={8} />
            </div>
            <div className="flex gap-2 mt-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} width={200} height={200} borderRadius={8} />
              ))}
            </div>
            <Skeleton height={200} borderRadius={8} />
          </div>
        ) : (
          <div className="flex flex-col gap-5 p-1">
            <div className="flex items-start justify-between gap-3">
              <div className="flex flex-col gap-1">
                <h2 className="text-display-2xs content-base-primary">{submission.task_title}</h2>
                {submission.task_description && (
                  <p className="text-body-regular-sm content-base-secondary">{submission.task_description}</p>
                )}
              </div>
              <Button variant="tertiary" size="md" className="shrink-0" onClick={handleOpenTask}>
                {t("fsmReview.openTask")}
              </Button>
            </div>

            <div className="flex flex-col">
              {(() => {
                const fields = [
                  {
                    key: "status",
                    label: t("fsmReview.status"),
                    value: statusConfig ? t(statusConfig.labelKey) : "—",
                    icon: Clock,
                    isBadge: true as const,
                    badgeConfig: statusConfig,
                  },
                  { key: "project", label: t("fsmReview.project"), value: submission.project_name, icon: Folder2 },
                  { key: "worker", label: t("fsmReview.worker"), value: submission.submitter_name, icon: Profile },
                  {
                    key: "submittedAt",
                    label: t("fsmReview.submittedAt"),
                    value: submission.submitted_at ? formatDateForDisplay(submission.submitted_at, true) : "—",
                    icon: Calendar,
                  },
                  {
                    key: "capturedAt",
                    label: t("fsmReview.capturedAt"),
                    value: submission.captured_at ? formatDateForDisplay(submission.captured_at, true) : "—",
                    icon: Camera,
                  },
                  {
                    key: "device",
                    label: t("fsmReview.device"),
                    value:
                      [submission.platform, submission.device_make, submission.device_model]
                        .filter(Boolean)
                        .join(", ") || "—",
                    icon: Mobile,
                  },
                  ...(hasCoordinates
                    ? [
                        {
                          key: "coordinates",
                          label: t("fsmReview.coordinates"),
                          value: `${submission.centroid_lat}, ${submission.centroid_lng}`,
                          icon: Location,
                        },
                      ]
                    : []),
                  ...(submission.status !== "pending_review" && submission.reviewed_by
                    ? [
                        {
                          key: "reviewedBy",
                          label: t("fsmReview.reviewedBy"),
                          value: submission.reviewer_name || "—",
                          icon: Profile,
                        },
                        {
                          key: "reviewedAt",
                          label: t("fsmReview.reviewedAt"),
                          value: submission.reviewed_at ? formatDateForDisplay(submission.reviewed_at, true) : "—",
                          icon: Calendar,
                        },
                      ]
                    : []),
                ];
                return fields.map((field, index, array) => {
                  const IconComponent = field.icon;
                  return (
                    <div
                      key={field.key}
                      className={cn(
                        "py-3 flex items-center gap-3",
                        index < array.length - 1 && "border-b surface-base-stroke",
                      )}>
                      <span className="content-action-brand">
                        <IconComponent size={16} color="currentColor" />
                      </span>
                      <span className="text-body-regular-md content-base-secondary flex-1">{field.label}</span>
                      {"isBadge" in field && field.isBadge && field.badgeConfig ? (
                        <Badge
                          variant="soft"
                          color={field.badgeConfig.color}
                          text={field.value}
                          icon={field.badgeConfig.icon}
                        />
                      ) : (
                        <p className="text-body-bold-md content-base-primary text-right">{field.value}</p>
                      )}
                    </div>
                  );
                });
              })()}
            </div>

            {submission.worker_note ? (
              <div className="flex flex-col gap-1.5">
                <span className="text-label-sm content-base-secondary">{t("fsmReview.workerNote")}</span>
                <div className="surface-secondary-fill radius-sm p-3">
                  <p className="text-body-regular-sm content-base-primary whitespace-pre-wrap">
                    {submission.worker_note}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                <span className="text-label-sm content-base-secondary">{t("fsmReview.workerNote")}</span>
                <p className="text-body-regular-sm content-base-low">{t("fsmReview.noWorkerNote")}</p>
              </div>
            )}

            {(submission.pdf_status === "pending" || submission.pdf_status === "generating") && (
              <p className="text-body-regular-sm content-base-low">{t("fsmReview.pdfGenerating")}</p>
            )}
            {submission.pdf_status === "failed" && (
              <p className="text-body-regular-sm content-action-negative">{t("fsmReview.pdfFailed")}</p>
            )}

            {hasProcessingPhotos && (
              <div className="surface-notice-subtle radius-sm px-3 py-2">
                <p className="text-body-regular-sm content-action-notice">{t("fsmReview.photoProcessingWarning")}</p>
              </div>
            )}

            {verificationPhotos.length > 0 && (
              <div className="flex flex-col gap-2">
                <span className="text-label-sm content-base-secondary">{t("fsmReview.verificationPhoto")}</span>
                <div className="flex gap-2">{renderPhotoTile(verificationPhotos[0])}</div>
              </div>
            )}

            {activityPhotos.length > 0 ? (
              <div className="flex flex-col gap-2">
                <span className="text-label-sm content-base-secondary">{t("fsmReview.activityPhotos")}</span>
                <div className="flex gap-2">{activityPhotos.slice(0, 3).map((photo) => renderPhotoTile(photo))}</div>
              </div>
            ) : verificationPhotos.length === 0 ? (
              <div className="flex flex-col gap-1.5">
                <span className="text-label-sm content-base-secondary">{t("fsmReview.activityPhotos")}</span>
                <p className="text-body-regular-sm content-base-low">{t("fsmReview.noPhotos")}</p>
              </div>
            ) : null}

            {hasCoordinates && (
              <div className="flex flex-col gap-2">
                <span className="text-label-sm content-base-secondary">{t("fsmReview.location")}</span>
                <style>{`.leaflet-container img { max-width: none !important; }`}</style>
                <div className="h-[250px] radius-md overflow-hidden">
                  <MapContainer
                    center={[parseFloat(submission.centroid_lat!), parseFloat(submission.centroid_lng!)]}
                    zoom={15}
                    style={{ height: "100%", width: "100%" }}
                    scrollWheelZoom={false}>
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={[parseFloat(submission.centroid_lat!), parseFloat(submission.centroid_lng!)]} />
                  </MapContainer>
                </div>
              </div>
            )}

            {submission.status === "rejected" && submission.rejection_reason && (
              <div className="flex flex-col gap-1.5">
                <span className="text-label-sm content-base-secondary">{t("fsmReview.rejectionReason")}</span>
                <div className="surface-negative-subtle radius-sm p-3">
                  <p className="text-body-regular-sm content-action-negative">{submission.rejection_reason}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </ModalForm>

      {showRejectModal && (
        <FSMRejectModal
          submissionId={submissionId}
          onClose={() => setShowRejectModal(false)}
          onSuccess={handleRejectSuccess}
          onError={handleRejectError}
        />
      )}

      {viewingPhoto && viewingPhoto.image_url && (
        <ImageViewer
          imageUrl={viewingPhoto.image_url}
          fileName={`photo-${viewingPhoto.id}`}
          onClose={() => setViewingPhoto(null)}
        />
      )}
    </>
  );
}

